/* eslint-disable no-nested-ternary */
import AssignmentIcon from '@mui/icons-material/Assignment';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import FolderIcon from '@mui/icons-material/Folder';
import ReplyIcon from '@mui/icons-material/Reply';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { styled } from '@mui/material/styles';
import { SvgIconProps } from '@mui/material/SvgIcon';
import Typography from '@mui/material/Typography';
import React from 'react';
import { Data } from '../elementsList-state';

const PREFIX = 'ElementList';

const classes = {
  propertiesContent: `${PREFIX}-propertiesContent`,
  headerContent: `${PREFIX}-headerContent`,
  nested: `${PREFIX}-nested`,
  paper: `${PREFIX}-paper`
};

const StyledCard = styled(Card)(({ theme }) => ({
  [`& .${classes.propertiesContent}`]: {
    flex: '1 1 auto',
    paddingTop: '0px',
    maxHeight: '100%',
    overflow: 'auto',
    '&::-webkit-scrollbar': {
      width: '7px'
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#888',
      borderRadius: '3px'
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: '#555'
    },
    '&::-webkit-scrollbar-track': {
      background: '#f1f1f1',
      borderRadius: '3px'
    },
    scrollbarColor: '#888 #f1f1f1',
    scrollbarWidth: 'thin'
  },

  [`& .${classes.headerContent}`]: { paddingBottom: '0px', flex: '0 1 auto' },

  [`& .${classes.nested}`]: {
    paddingLeft: theme.spacing(4)
  },

  [`&.${classes.paper}`]: {
    backgroundColor: 'transparent',
    display: 'flex',
    flexFlow: 'column',
    color: 'inherit'
  }
}));

type Icon = React.ElementType<SvgIconProps<'svg', {}>>;

interface ElementListProps {
  data?: Data[];
  title?: string;
  icon?: Icon;
}

const MapSingleLine = ({
  item,
  // eslint-disable-next-line no-shadow
  Icon,
  nested = false
}: {
  item: Data;
  Icon: Icon;
  nested?: boolean;
}) => {
  return (
    <ListItem
      data-testid="ElementListItem"
      key={item.id}
      style={{ fontSize: '16px' }}
      button
      className={nested ? classes.nested : ''}
      component="a"
      href={item.link || (item.informationItems && item.informationItems[0].link) || undefined}
      target="_blank"
      rel="noopener noreferrer"
    >
      <ListItemIcon>
        <Icon fontSize="inherit" />
      </ListItemIcon>
      <ListItemText
        disableTypography
        primary={
          <Typography
            color="inherit"
            variant="body2"
            style={{ fontSize: '12px', overflowWrap: 'anywhere' }}
          >
            {item.title && item.title.length > 0
              ? item.title
              : item.name && item.name.length > 0
              ? item.name
              : 'No name'}
          </Typography>
        }
      />
      {item.link || (item.informationItems && item.informationItems[0].link) ? (
        <ReplyIcon style={{ transform: 'rotateY(180deg)' }} fontSize="inherit" />
      ) : null}
    </ListItem>
  );
};

const MapItem = ({ item, icon }: { item: Data; icon: Icon }) => {
  const [open, setOpen] = React.useState(false);

  const handleClick = () => {
    setOpen(!open);
  };

  return (
    <React.Fragment key={item.id}>
      <ListItem
        style={{ fontSize: '16px' }}
        button
        onClick={handleClick}
        data-testid="ElementListItem"
      >
        <ListItemIcon>
          <FolderIcon fontSize="inherit" />
        </ListItemIcon>
        <ListItemText
          disableTypography
          primary={
            <Typography variant="body2" style={{ fontSize: '12px', overflowWrap: 'anywhere' }}>
              {item.name && item.name.length > 0 ? item.name : 'No name'}
            </Typography>
          }
        />
        {open ? <ExpandLess fontSize="inherit" /> : <ExpandMore fontSize="inherit" />}
      </ListItem>
      <Collapse in={open} timeout={0} unmountOnExit>
        <List component="div" disablePadding>
          {item.informationItems &&
            item.informationItems.map((infoItem: Data) => (
              <MapSingleLine
                key={infoItem.id}
                item={infoItem}
                nested
                Icon={icon || AssignmentIcon}
              />
            ))}
        </List>
      </Collapse>
    </React.Fragment>
  );
};

const ElementList = ({ title, data, icon }: ElementListProps) => {
  return (
    <StyledCard className={classes.paper} data-testid="elementList" elevation={0}>
      {title && title?.length > 0 ? (
        <CardHeader
          title={title}
          className={classes.headerContent}
          titleTypographyProps={{
            align: 'left',
            variant: 'subtitle1',
            style: { overflowWrap: 'anywhere' }
          }}
          data-testid="elementList-title"
        />
      ) : null}
      <CardContent className={classes.propertiesContent}>
        <List>
          {data &&
            data.map((item: Data) =>
              item.informationItems && item.informationItems.length > 1 ? (
                <MapItem key={item.id} item={item} icon={icon || AssignmentIcon} />
              ) : (
                <MapSingleLine key={item.id} item={item} Icon={icon || AssignmentIcon} />
              )
            )}
        </List>
      </CardContent>
    </StyledCard>
  );
};

export default ElementList;
